import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Loader2, MessageSquareText, RotateCcw, Send, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { EmptyState, StatusBadge } from './components/marketing-ui';
import {
  useCollaboratorsQuery,
  useCreateWorkFeedbackMutation,
  useResolveWorkFeedbackMutation,
  useWorkFeedbackQuery,
} from '@/client/api/marketing/crew';
import {
  useContentQuery,
  usePlacementsQuery,
  useSocialPostsQuery,
} from '@/client/api/marketing/studio';
import { useMediaOutletsQuery } from '@/client/api/marketing/sources';
import type { ApiWorkFeedback } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  WORK_FEEDBACK_STATUS_BADGE,
  WORK_FEEDBACK_STATUS_LABEL,
  WORK_FEEDBACK_TARGET_LABEL,
  type WorkFeedbackTarget,
} from '@/client/types/marketing';

/**
 * Crew → Feedback, on real data.
 *
 * WIP discussion, distinct from Approvals: this is where someone asks for a
 * second pair of eyes; Approvals is the gate that stamps a record.
 *
 * Resolving goes through the resolve endpoint rather than a PATCH, because
 * `resolved_at` is server-set — a caller must not be able to backdate when a
 * review point was closed. Reopening clears both the timestamp and the resolver,
 * so a thread closed twice does not keep the first resolution's attribution.
 *
 * "Viewing as" is a lens, not an identity: it filters to threads tagging that
 * person. It does not decide who anything is attributed to — the server takes
 * the author from the session.
 */

const FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'tagged', label: 'Tagged to check' },
  { id: 'all', label: 'All' },
  { id: 'resolved', label: 'Resolved' },
] as const;

type FilterId = (typeof FILTERS)[number]['id'];

const taggedIds = (f: ApiWorkFeedback): number[] =>
  Array.isArray(f.tagged_ids) ? (f.tagged_ids as unknown[]).map(Number).filter(Number.isFinite) : [];

const FeedbackPage = () => {
  const feedbackQ = useWorkFeedbackQuery({ limit: 200 });
  const crew = useCollaboratorsQuery({ limit: 200 });
  const contentQ = useContentQuery({ limit: 200 });
  const postsQ = useSocialPostsQuery({ limit: 200 });
  const placementsQ = usePlacementsQuery({ limit: 200 });
  const outlets = useMediaOutletsQuery({ limit: 200 });
  const resolve = useResolveWorkFeedbackMutation();
  const postNote = useCreateWorkFeedbackMutation();

  const [filter, setFilter] = useState<FilterId>('open');
  const [viewerId, setViewerId] = useState<number | ''>('');
  const [selected, setSelected] = useState<ApiWorkFeedback | null>(null);
  const [draft, setDraft] = useState('');

  const feedback = feedbackQ.data?.items ?? [];
  const collaborators = crew.data?.items ?? [];

  const nameOf = (mcid: number | null) =>
    collaborators.find((c) => c.mcid === mcid)?.name ?? 'Unknown';

  const targetTitle = (f: ApiWorkFeedback) => {
    if (f.target_type === 'content') {
      return contentQ.data?.items.find((c) => c.mciid === f.target_id)?.title ?? `#${f.target_id}`;
    }
    if (f.target_type === 'social_post') {
      const p = postsQ.data?.items.find((x) => x.mspid === f.target_id);
      return p ? (p.caption ?? '(no caption)').slice(0, 72) : `#${f.target_id}`;
    }
    const pl = placementsQ.data?.items.find((x) => x.mtpid === f.target_id);
    if (!pl) return `#${f.target_id}`;
    const outlet = outlets.data?.items.find((o) => o.moid === pl.moid)?.name ?? `Outlet ${pl.moid}`;
    return `${outlet} · ${pl.medium}`;
  };

  const targetHref = (f: ApiWorkFeedback) => {
    if (f.target_type === 'content') return `/admin/marketing/studio/content/${f.target_id}`;
    if (f.target_type === 'social_post') return '/admin/marketing/studio/social';
    return '/admin/marketing/studio/traditional';
  };

  const filtered = useMemo(() => {
    let list = [...feedback];
    if (filter === 'open') list = list.filter((f) => f.status === 'open');
    if (filter === 'resolved') list = list.filter((f) => f.status === 'resolved');
    if (filter === 'tagged') {
      list = list.filter(
        (f) => f.status === 'open' && viewerId !== '' && taggedIds(f).includes(viewerId),
      );
    }
    return list.sort((a, b) => (b.date_added ?? '').localeCompare(a.date_added ?? ''));
  }, [feedback, filter, viewerId]);

  const viewerOptions = useMemo(
    () => [
      { value: '', label: 'Anyone' },
      ...collaborators
        .filter((c) => c.active)
        .map((c) => ({ value: String(c.mcid), label: c.name })),
    ],
    [collaborators],
  );

  const filtersActive = filter !== 'open' || viewerId !== '';

  const clearFilters = () => {
    setFilter('open');
    setViewerId('');
  };

  /** Every note on the same work item, so the aside reads as a thread. */
  const thread = useMemo(() => {
    if (!selected) return [];
    return feedback
      .filter(
        (f) => f.target_type === selected.target_type && f.target_id === selected.target_id,
      )
      .sort((a, b) => (a.date_added ?? '').localeCompare(b.date_added ?? ''));
  }, [feedback, selected]);

  const stats = useMemo(() => {
    const n = (v: number) => (feedbackQ.isLoading ? 0 : v);
    return [
      { key: 'open', label: 'Open', value: n(feedback.filter((f) => f.status === 'open').length) },
      {
        key: 'tagged',
        label: 'Tagged for you',
        value: n(
          feedback.filter(
            (f) => f.status === 'open' && viewerId !== '' && taggedIds(f).includes(viewerId),
          ).length,
        ),
      },
      {
        key: 'resolved',
        label: 'Resolved',
        value: n(feedback.filter((f) => f.status === 'resolved').length),
      },
      { key: 'total', label: 'Total', value: n(feedback.length) },
    ];
  }, [feedback, viewerId, feedbackQ.isLoading]);

  const byType = (type: WorkFeedbackTarget) =>
    filtered.filter((f) => f.target_type === type).length;

  const toggleResolved = (f: ApiWorkFeedback) =>
    resolve.mutate(
      { mwfid: f.mwfid, resolved: f.status !== 'resolved' },
      {
        onSuccess: () =>
          toast.success(f.status === 'resolved' ? 'Thread reopened' : 'Thread resolved'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the thread')),
      },
    );

  const addNote = () => {
    if (!selected || !draft.trim()) return;
    postNote.mutate(
      {
        target_type: selected.target_type,
        target_id: selected.target_id,
        body: draft.trim(),
      },
      {
        onSuccess: () => {
          setDraft('');
          toast.success('Note added');
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add the note')),
      },
    );
  };

  return (
    <MarketingPageShell
      title="Feedback"
      description="Comments and check-requests on work in progress — tag people to review copy, assets, and placements before Approvals."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Crew' },
        { label: 'Feedback' },
      ]}
      icon={MessageSquareText}
      stats={stats}
    >
      <p className="mb-4 text-sm text-gray-600">
        <strong className="font-medium text-gray-800">Contacts</strong> are source people ·{' '}
        <strong className="font-medium text-gray-800">Feedback</strong> is WIP discussion ·{' '}
        <strong className="font-medium text-gray-800">Approvals</strong> is the formal gate.
      </p>

      <MarketingFilterPanel
        summary={marketingMatchSummary(
          filtered.length,
          feedback.length,
          'thread',
          feedbackQ.isLoading,
        )}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filter === f.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <MarketingFilterSelect
          options={viewerOptions}
          value={viewerId === '' ? '' : String(viewerId)}
          onChange={(value) => setViewerId(value ? Number(value) : '')}
          placeholder="Viewing as anyone"
          clearValue=""
          loading={crew.isLoading}
          className={`${marketingFilterSelectClass} sm:min-w-[12rem]`}
        />
        <span className="self-center text-xs text-gray-400">
          Content {byType('content')} · Social {byType('social_post')} · Traditional{' '}
          {byType('traditional_placement')}
        </span>
      </MarketingFilterPanel>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          {feedbackQ.isLoading ? (
            <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Loading feedback…
            </div>
          ) : feedbackQ.isError ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
              <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
              <div className="font-medium text-amber-800">Could not load feedback</div>
              <div className="mt-1 text-xs text-gray-500">
                {getApiErrorMessage(feedbackQ.error, 'The marketing API did not respond')}
              </div>
            </div>
          ) : feedback.length === 0 ? (
            <EmptyState label="No feedback yet — open a piece of work and leave the first note." />
          ) : filtered.length === 0 ? (
            <EmptyState label="No feedback in this view." />
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Feedback</th>
                    <th className="px-4 py-3 font-medium">On</th>
                    <th className="px-4 py-3 font-medium">Tagged</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((f) => {
                    const tagged = taggedIds(f);
                    return (
                      <tr
                        key={f.mwfid}
                        className={cn(
                          'cursor-pointer hover:bg-gray-50',
                          selected?.mwfid === f.mwfid && 'bg-blue-50/50',
                        )}
                        onClick={() => setSelected(f)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{nameOf(f.author_mcid)}</p>
                          <p className="line-clamp-2 text-gray-600">{f.body}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-[11px] uppercase tracking-wide text-gray-400">
                            {WORK_FEEDBACK_TARGET_LABEL[f.target_type as WorkFeedbackTarget] ??
                              f.target_type}
                          </p>
                          <Link
                            to={targetHref(f)}
                            onClick={(e) => e.stopPropagation()}
                            className="line-clamp-2 text-blue-700 hover:underline"
                          >
                            {targetTitle(f)}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          {tagged.length === 0 ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {tagged.map((id) => (
                                <span
                                  key={id}
                                  className={cn(
                                    'rounded px-1.5 py-0.5 text-[11px]',
                                    id === viewerId
                                      ? 'bg-sky-100 text-sky-800'
                                      : 'bg-gray-100 text-gray-700',
                                  )}
                                >
                                  @{nameOf(id).split(' ')[0]}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={WORK_FEEDBACK_STATUS_LABEL[f.status] ?? f.status}
                            className={WORK_FEEDBACK_STATUS_BADGE[f.status] ?? ''}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {f.date_added ? new Date(f.date_added).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleResolved(f);
                            }}
                          >
                            {f.status === 'resolved' ? (
                              <>
                                <RotateCcw className="h-3.5 w-3.5" /> Reopen
                              </>
                            ) : (
                              <>
                                <Check className="h-3.5 w-3.5" /> Resolve
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          {selected ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">
                  {WORK_FEEDBACK_TARGET_LABEL[selected.target_type as WorkFeedbackTarget] ??
                    selected.target_type}
                </p>
                <Link
                  to={targetHref(selected)}
                  className="text-sm font-medium text-blue-700 hover:underline"
                >
                  {targetTitle(selected)}
                </Link>
              </div>

              <ul className="max-h-[24rem] space-y-2 overflow-y-auto">
                {thread.map((note) => (
                  <li
                    key={note.mwfid}
                    className={cn(
                      'rounded-md border px-2.5 py-2 text-sm',
                      note.status === 'resolved'
                        ? 'border-gray-100 bg-gray-50 text-gray-500'
                        : 'border-gray-200',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-gray-700">
                        {nameOf(note.author_mcid)}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {note.date_added ? new Date(note.date_added).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="mt-1 leading-snug">{note.body}</p>
                    {note.status === 'resolved' && note.resolved_at && (
                      <p className="mt-1 text-[10px] text-gray-400">
                        Resolved {new Date(note.resolved_at).toLocaleString()}
                        {note.resolved_by_mcid ? ` by ${nameOf(note.resolved_by_mcid)}` : ''}
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex gap-2 border-t border-gray-100 pt-3">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      addNote();
                    }
                  }}
                  placeholder="Add a note…"
                  className="h-9 flex-1 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2"
                />
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5"
                  disabled={!draft.trim() || postNote.isPending}
                  onClick={addNote}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Select a row to open the thread for that work item.
            </p>
          )}
        </aside>
      </div>
    </MarketingPageShell>
  );
};

export default FeedbackPage;
